import { message } from "antd";
import {
  CreditworthinessEstablishedRating,
  ResultsScoring,
} from "../../types/scoring.type";
import { api } from "../apiCore";
import { ApplicationBank } from "../../types/applicationBank.type";
import { applicationBankApi } from "./applicationBank.api";

export interface StopFactorsChange {
  type: string;
  results: {
    key: string;
    comment: string;
  }[];
  commentStopFactors?: string;
}

export interface BusinessRisksChange {
  type: string;
  results: {
    key: string;
    value: number;
  }[];
  commentBusinessRisks?: string;
}

export interface CreditWorthinessChange {
  creditworthinessResults: CreditworthinessEstablishedRating[];
  commentCreditworthiness?: string;
  commentSettlementRating?: string;
}

export interface ChangeApplicationDataToBankDto {
  app_id: number;
  data: BusinessRisksChange | StopFactorsChange | CreditWorthinessChange;
}

export const scoringApi = api.injectEndpoints({
  endpoints: (builder) => ({
    sendDataToScoring: builder.mutation<ResultsScoring, { app_id: number }>({
      query: (value) => ({
        url: `/auth/scoring-processing/${value.app_id}`,
        method: "POST",
        body: {
          status: "revision",
        },
      }),
      onQueryStarted: async ({ app_id }, { dispatch, queryFulfilled }) => {
        try {
          const { data: scoringData } = await queryFulfilled;

          dispatch(
            applicationBankApi.util.updateQueryData(
              "getApplicationDataToBank",
              app_id,
              (draft) => {
                draft.scoring_result.data = scoringData;
              }
            )
          );
          message.success("Данные отправлены на скоринг");
        } catch {
          message.error("Не удалось отправить данные на скоринг");
        }
      },
    }),

    changeDataToScoring: builder.mutation<
      ResultsScoring,
      ChangeApplicationDataToBankDto
    >({
      query: ({ data, app_id }) => ({
        url: `/auth/scoring-processing/${app_id}/update-results`,
        method: "POST",
        body: {
          ...data,
        },
      }),
      onQueryStarted: async ({ app_id }, { dispatch, queryFulfilled }) => {
        try {
          const { data: scoringData } = await queryFulfilled;

          dispatch(
            applicationBankApi.util.updateQueryData(
              "getApplicationDataToBank",
              app_id,
              (draft) => {
                draft.scoring_result.data = scoringData;
              }
            )
          );
          message.success("Данные успешно сохранены");
        } catch {
          message.error("Не удалось сохранить данные");
        }
      },
    }),

    changeDataToScoringCreditWorthiness: builder.mutation<
      ResultsScoring,
      ChangeApplicationDataToBankDto
    >({
      query: ({ data, app_id }) => ({
        url: `/auth/scoring-processing/${app_id}/update-creditworthiness-results`,
        method: "POST",
        body: {
          ...data,
        },
      }),
      onQueryStarted: async ({ app_id }, { dispatch, queryFulfilled }) => {
        try {
          const { data: scoringData } = await queryFulfilled;

          dispatch(
            applicationBankApi.util.updateQueryData(
              "getApplicationDataToBank",
              app_id,
              (draft) => {
                draft.scoring_result.data = scoringData;
              }
            )
          );
          message.success("Данные успешно сохранены");
        } catch {
          message.error("Не удалось сохранить данные");
        }
      },
    }),

    // сохранить заключение профсуждения
    updateCreditWorthinessConclusion: builder.mutation<
      ApplicationBank,
      {
        applicationBankId: number;
        body: {
          commentProfessionalJudgment: string;
        };
      }
    >({
      query: ({ applicationBankId, body }) => {
        return {
          url: `auth/scoring-processing/${applicationBankId}/update-creditworthiness-conclusion`,
          method: "POST",
          body: body,
        };
      },
      invalidatesTags: ["ApplicationBank"],
    }),

    // отправить на повторный скоринг
    sendDataToRepeatedScoring: builder.mutation<
      ResultsScoring,
      { applicationBankId: number }
    >({
      query: (value) => ({
        url: `/auth/scoring-processing/${value.applicationBankId}`,
        method: "POST",
        body: {
          status: "repeated",
        },
      }),
      onQueryStarted: async (
        { applicationBankId },
        { dispatch, queryFulfilled }
      ) => {
        try {
          const { data: scoringData } = await queryFulfilled;

          dispatch(
            applicationBankApi.util.updateQueryData(
              "getApplicationDataToBank",
              applicationBankId,
              (draft) => {
                draft.scoring_result.data = scoringData;
              }
            )
          );
          message.success("Заявка успешно отправлена на повторный скоринг");
        } catch {
          message.error("Не удалось отправить заявку на повторный скоринг");
        }
      },
    }),
  }),
});

export const {
  useSendDataToScoringMutation,
  useChangeDataToScoringMutation,
  useChangeDataToScoringCreditWorthinessMutation,
  useSendDataToRepeatedScoringMutation,
  useUpdateCreditWorthinessConclusionMutation,
} = scoringApi;
